import { fetchPatientList } from '../../../services/apiService';
import type {
  ApiPatient,
  DocumentItem,
  DocumentKind,
  ExtractReportResponse,
  FetchPatientsResponse,
  PatientSummary,
  QuestionnaireField,
  QuestionnaireSection,
  ResponseVersion,
  SummaryLogItem,
  ParsedSummaryAiNotes,
} from './DocumentIntelligencePatientDetail.types';

import {
  formatDateDMY,
  formatDateYMD
} from '../../../utils/dateUtils';

const OCR_EXTRACT_ENDPOINT =
  'https://vidai-ai.in/api/v1/emr-ocr/extract-questionnaire';

const SAVE_INTELLIGENCE_ENDPOINT =
  'https://99999.preview-api.vidaisolutions.com/api/ai-components/save-intelligence';

const SAVE_SUMMARY_NOTES_ENDPOINT =
  'https://99999.preview-api.vidaisolutions.com/api/patients';

const CYCLE_ACTIVE_CHECK_ENDPOINT =
  'https://99999.preview-api.vidaisolutions.com/api/cycle/is-cycle-active/';

export const firstNonEmpty = (...values: unknown[]): string => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }

    if (typeof value === 'number' && !Number.isNaN(value)) {
      return String(value);
    }
  }

  return '';
};

export const toISODate = (date: Date): string => {
  return formatDateYMD(date);
};

export const getDateRangeForLastMonth = () => {
  const today = new Date();

  const endDate = formatDateYMD(today);

  const lastMonth = new Date(today);
  lastMonth.setMonth(today.getMonth() - 1);

  const startDate = formatDateYMD(lastMonth);

  return { startDate, endDate };
};

export const formatDisplayDate = (value?: string | null): string => {
  if (!value) return '-';

  const formatted = formatDateDMY(value);
  return formatted || '-';
};

export const getFullName = (first?: string, last?: string): string =>
  `${first ?? ''} ${last ?? ''}`.trim() || '-';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getAvatarUrl = (obj?: Record<string, any> | null): string => {
  return (
    firstNonEmpty(obj?.photo, obj?.profile_image, obj?.avatar, obj?.image) || ''
  );
};

export const getInitials = (name: string): string => {
  const parts = name
    .split(' ')
    .map((item) => item.trim())
    .filter(Boolean)
    .slice(0, 2);

  if (!parts.length) return 'P';

  return parts.map((part) => part.charAt(0).toUpperCase()).join('');
};

export const getAgeLabel = (patient: ApiPatient): string => {
  const directAge = firstNonEmpty(patient.age, patient.age_label);
  if (directAge) {
    return /\b(year|years|yr|yrs)\b/i.test(directAge)
      ? directAge
      : `${directAge} Years`;
  }

  const dob = firstNonEmpty(
    patient.date_of_birth,
    patient.dob,
    patient.birth_date
  );

  if (!dob) return '-';

  const birthDate = new Date(dob);
  if (Number.isNaN(birthDate.getTime())) return '-';

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
  ) {
    age -= 1;
  }

  return age >= 0 ? `${age} Years` : '-';
};

export const getAllergyLabel = (patient: ApiPatient): string => {
  if (typeof patient.has_allergy === 'boolean') {
    return patient.has_allergy ? 'Yes' : 'No';
  }

  if (typeof patient.allergy === 'boolean') {
    return patient.allergy ? 'Yes' : 'No';
  }

  if (Array.isArray(patient.allergies)) {
    return patient.allergies.length > 0 ? 'Yes' : 'No';
  }

  const allergyText = firstNonEmpty(
    patient.allergy,
    patient.allergies,
    patient.allergy_status
  );

  if (!allergyText) return 'No';

  if (/^(no|none|nil|na|n\/a)$/i.test(allergyText)) {
    return 'No';
  }

  return 'Yes';
};

export const mapPatientToSummary = (patient: ApiPatient): PatientSummary => ({
  id: patient.id,
  name: getFullName(patient.first_name, patient.last_name),
  patientAvatar: getAvatarUrl(patient),
  partnerAvatar: getAvatarUrl(patient.partner),
  age: getAgeLabel(patient),
  sexAssignedAtBirth:
    firstNonEmpty(
      patient.sex_assigned_at_birth,
      patient.sex_at_birth,
      patient.gender_label,
      patient.gender__name,
      patient.gender
    ) || '-',
  mrn:
    firstNonEmpty(
      patient.mrn,
      patient.medical_record_number,
      patient.medical_record_no,
      patient.patient_code
    ) || '-',
  allergy: getAllergyLabel(patient),
  lastModified: formatDisplayDate(
    firstNonEmpty(
      patient.modified_on,
      patient.updated_on,
      patient.last_modified,
      patient.created_on
    )
  ),
  doctor: firstNonEmpty(patient.doctor, patient.doctor_name) || '-',
});

export const detectDocumentKind = (fileName: string): DocumentKind => {
  const lower = fileName.toLowerCase();

  if (lower.endsWith('.pdf')) return 'pdf';
  if (
    lower.endsWith('.xls') ||
    lower.endsWith('.xlsx') ||
    lower.endsWith('.csv')
  ) {
    return 'sheet';
  }
  if (
    lower.endsWith('.png') ||
    lower.endsWith('.jpg') ||
    lower.endsWith('.jpeg') ||
    lower.endsWith('.webp')
  ) {
    return 'image';
  }

  return 'doc';
};

export const formatFileSize = (sizeInBytes: number): string => {
  if (!sizeInBytes || sizeInBytes <= 0) return '0 MB';
  return `${(sizeInBytes / (1024 * 1024)).toFixed(2)} MB`;
};

export const buildPatientLookupQuery = (
  startDate: string,
  endDate: string,
  limit: number,
  offset = 0
): string => {
  const params = new URLSearchParams();
  params.append('patient_type', 'patient');
  params.append('created_on_after', startDate);
  params.append('created_on_before', endDate);
  params.append('limit', String(limit));
  params.append('offset', String(offset));
  return params.toString();
};

export const findPatientById = async (
  clinicId: string,
  patientId: string | number,
  startDate: string,
  endDate: string
): Promise<ApiPatient> => {
  const primaryQuery = buildPatientLookupQuery(startDate, endDate, 25, 0);
  const primaryResponse = (await fetchPatientList(
    primaryQuery,
    clinicId
  )) as FetchPatientsResponse;

  let matchedPatient = (primaryResponse?.objects ?? []).find(
    (item) => String(item.id) === String(patientId)
  );

  if (matchedPatient) {
    return matchedPatient;
  }

  const fallbackQuery = buildPatientLookupQuery(startDate, endDate, 300, 0);
  const fallbackResponse = (await fetchPatientList(
    fallbackQuery,
    clinicId
  )) as FetchPatientsResponse;

  matchedPatient = (fallbackResponse?.objects ?? []).find(
    (item) => String(item.id) === String(patientId)
  );

  if (!matchedPatient) {
    throw new Error('Patient not found');
  }

  return matchedPatient;
};

export const mapUploadedFilesToDocuments = (files: File[]): DocumentItem[] => {
  return files.map((file, index) => ({
    id: `${file.name}-${file.lastModified}-${index}`,
    title: file.name,
    size: formatFileSize(file.size),
    kind: detectDocumentKind(file.name),
    source: 'uploaded',
    file,
    previewUrl: URL.createObjectURL(file),
    mimeType: file.type,
    lastModified: file.lastModified,
  }));
};

export const filterDocuments = (
  documents: DocumentItem[],
  search: string
): DocumentItem[] => {
  const q = search.trim().toLowerCase();

  if (!q) return documents;

  return documents.filter((doc) => doc.title.toLowerCase().includes(q));
};

export const isPreviewableDocument = (doc?: DocumentItem | null): boolean => {
  if (!doc) return false;
  return doc.kind === 'pdf' || doc.kind === 'image';
};

export const isExtractableDocument = (doc?: DocumentItem | null): boolean => {
  if (!doc) return false;
  return doc.kind === 'pdf' || doc.kind === 'image';
};

const getAccessToken = (): string => {
  return (
    localStorage.getItem('accessToken') ||
    localStorage.getItem('token') ||
    sessionStorage.getItem('accessToken') ||
    sessionStorage.getItem('token') ||
    ''
  );
};

const sortByDisplayOrder = <T extends { display_order?: number }>(
  items: T[]
): T[] => {
  return [...items].sort(
    (a, b) =>
      (a.display_order ?? Number.MAX_SAFE_INTEGER) -
      (b.display_order ?? Number.MAX_SAFE_INTEGER)
  );
};

const toEditableString = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '';
  if (Array.isArray(value)) {
    return value
      .map((item) => (typeof item === 'object' ? JSON.stringify(item) : String(item)))
      .join(', ');
  }
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const parseValueForEmr = (field: QuestionnaireField, rawValue: string): unknown => {
  const trimmed = rawValue.trim();

  if (!trimmed) {
    return null;
  }

  const uiType = (field.ui_type || '').toLowerCase();
  const dataType = (field.data_type || '').toLowerCase();

  if (uiType === 'number' || dataType === 'integer' || dataType === 'number') {
    const parsedNumber = Number(trimmed);
    return Number.isFinite(parsedNumber) ? parsedNumber : null;
  }

  if (dataType === 'boolean') {
    if (/^(true|yes|1)$/i.test(trimmed)) return true;
    if (/^(false|no|0)$/i.test(trimmed)) return false;
  }

  return trimmed;
};

const getDocumentConfidence = (response: ExtractReportResponse): number => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const directConfidence = Number((response as any)?.document_confidence);

  if (Number.isFinite(directConfidence)) {
    return directConfidence;
  }

  const confidences = (response.sections ?? [])
    .flatMap((section) => section.fields ?? [])
    .map((field) => Number(field.ai_metadata?.confidence))
    .filter((value) => Number.isFinite(value));

  if (!confidences.length) return 0.5;

  const average =
    confidences.reduce((sum, value) => sum + value, 0) / confidences.length;

  return Number(average.toFixed(2));
};

const parseApiError = async (response: Response) => {
  const contentType = response.headers.get('content-type') || '';
  const result = contentType.includes('application/json')
    ? await response.json()
    : await response.text();

  if (typeof result === 'string') {
    return result || `Request failed with status ${response.status}`;
  }

  return (
    result?.message ||
    result?.detail ||
    result?.error ||
    `Request failed with status ${response.status}`
  );
};

const parseBooleanLike = (value: unknown): boolean | null => {
  if (typeof value === 'boolean') return value;

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();

    if (['true', 'yes', '1', 'active'].includes(normalized)) return true;
    if (['false', 'no', '0', 'inactive'].includes(normalized)) return false;
  }

  if (typeof value === 'number') {
    if (value === 1) return true;
    if (value === 0) return false;
  }

  return null;
};

const extractIsCycleActive = (payload: unknown): boolean => {
  const direct = parseBooleanLike(payload);
  if (direct !== null) return direct;

  if (Array.isArray(payload)) {
    return payload.some((item) => extractIsCycleActive(item));
  }

  if (!payload || typeof payload !== 'object') {
    return false;
  }

  const record = payload as Record<string, unknown>;

  const candidateKeys = [
    'is_cycle_active',
    'isCycleActive',
    'has_active_cycle',
    'hasActiveCycle',
    'active',
    'is_active',
    'status',
  ];

  for (const key of candidateKeys) {
    const parsed = parseBooleanLike(record[key]);
    if (parsed !== null) return parsed;

    if (typeof record[key] === 'string') {
      const normalized = String(record[key]).trim().toLowerCase();
      if (normalized === 'active') return true;
      if (normalized === 'inactive') return false;
    }
  }

  const nestedCandidates = [
    record.data,
    record.result,
    record.results,
    record.cycle,
    record.cycles,
    record.object,
  ];

  for (const candidate of nestedCandidates) {
    const parsed = extractIsCycleActive(candidate);
    if (parsed) return true;
  }

  return false;
};


type ExtractQuestionnaireParams = {
  document: File;
  documentId: string;
  patientId: string | number;
  responseVersion: ResponseVersion;
  includeUnanswered?: boolean;
  documentType: string;
};

export const extractQuestionnaireFromDocument = async ({
  document,
  documentId,
  patientId,
  responseVersion,
  includeUnanswered = true,
  documentType,
}: ExtractQuestionnaireParams): Promise<ExtractReportResponse> => {
  const token = getAccessToken();

  const formData = new FormData();
  formData.append('document', document);
  formData.append('document_id', String(documentId));
  formData.append('patient_id', String(patientId));

  const queryParams = new URLSearchParams({
    response_version: responseVersion,
    include_unanswered: String(includeUnanswered),
    document_type: documentType,
  });

  const response = await fetch(
    `${OCR_EXTRACT_ENDPOINT}?${queryParams.toString()}`,
    {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        ...(token ? { Authorization: 'Bearer your-bearer-token-here' } : {}),
      },
      body: formData,
    }
  );

  if (!response.ok) {
    throw new Error(await parseApiError(response));
  }

  return (await response.json()) as ExtractReportResponse;
};

type SaveExtractedDocumentParams = {
  patientId: string | number;
  documentType: string;
  extractionResponse: ExtractReportResponse;
  editableFieldValues: Record<string, string>;
};

export const saveExtractedDocumentIntelligenceToEMR = async ({
  patientId,
  documentType,
  extractionResponse,
  editableFieldValues,
}: SaveExtractedDocumentParams) => {
  const token = getAccessToken();

  if (!token) {
    throw new Error('Authentication token not found. Please login again.');
  }

  const resolvedDocumentType =
    documentType || extractionResponse.document_type || 'medical_history';

  const sections = sortByDisplayOrder(extractionResponse.sections ?? []).map(
    (section: QuestionnaireSection) => ({
      section_key: section.section_key,
      section_title: section.section_title,
      display_order: section.display_order,
      fields: sortByDisplayOrder(section.fields ?? []).map(
        (field: QuestionnaireField) => ({
          field_key: field.field_key,
          label: field.label,
          ui_type: field.ui_type,
          data_type: field.data_type,
          display_order: field.display_order,
          value: parseValueForEmr(
            field,
            editableFieldValues[field.field_key] ?? toEditableString(field.value)
          ),
          options: field.options ?? null,
          ai_metadata: field.ai_metadata ?? null,
        })
      ),
    })
  );

  const payload = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    schema_version: (extractionResponse as any)?.schema_version || '3.0',
    document_type: resolvedDocumentType,
    document_confidence: getDocumentConfidence(extractionResponse),
    sections,
    meta: extractionResponse.meta ?? null,
  };

  const url = `${SAVE_INTELLIGENCE_ENDPOINT}/${encodeURIComponent(
    String(patientId)
  )}/?document_type=${encodeURIComponent(resolvedDocumentType)}`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });

  const contentType = response.headers.get('content-type') || '';
  const result = contentType.includes('application/json')
    ? await response.json()
    : await response.text();

  if (!response.ok) {
    const message =
      typeof result === 'string'
        ? result
        : result?.message ||
          result?.detail ||
          result?.error ||
          'Failed to save extracted data to EMR';

    throw new Error(message);
  }

  return result;
};

type BuildSummaryNotesStringParams = {
  selectedTypeLabel: string;
  documentType: string;
  patientId: string | number;
  documentTitle?: string;
  summaryBullets: string[];
  extractionResponse: ExtractReportResponse;
  editableFieldValues?: Record<string, string>;
  reason?: 'manual_summary' | 'no_active_cycle_fallback';
  cycleActive?: boolean | null;
};

export const buildDocumentIntelligenceSummaryNotesString = ({
  selectedTypeLabel,
  documentType,
  patientId,
  documentTitle,
  summaryBullets,
  extractionResponse,
  editableFieldValues,
  reason = 'manual_summary',
  cycleActive = null,
}: BuildSummaryNotesStringParams): string => {
  return JSON.stringify({
    source: 'document_intelligence',
    title: `${selectedTypeLabel} AI Summary`,
    patient_id: String(patientId),
    document_type: documentType,
    document_title: documentTitle || null,
    summary_bullets: summaryBullets,
    extraction_meta: extractionResponse.meta ?? null,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    schema_version: (extractionResponse as any)?.schema_version || '3.0',
    parser_response: extractionResponse,
    edited_field_values: editableFieldValues ?? null,
    reason,
    cycle_active: cycleActive,
    created_at: new Date().toISOString(),
  });
};

type SaveSummaryNotesParams = {
  patientId: string;
  aiNotes: string;
  duration?: number;
  type?: string;
  document_name?: string;
  document_type?: string;
  doctor_name?: string;
  source?: string;
  call_trascript?: string;
};
export const saveDocumentIntelligenceSummaryToNotes = async ({
  patientId,
  aiNotes,
  duration,
  type,
  document_name,
  document_type,
  doctor_name,
  source,
  call_trascript,
}: SaveSummaryNotesParams) => {
  const token = getAccessToken();

  if (!token) {
    throw new Error('Authentication token not found. Please login again.');
  }

  const url = `${SAVE_SUMMARY_NOTES_ENDPOINT}/${encodeURIComponent(
    String(patientId)
  )}/summary-save/`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      ai_notes: aiNotes,
      duration,
      type,
      document_name,
      document_type,
      doctor_name,
      source,
      call_trascript,
    }),
  });

  const contentType = response.headers.get('content-type') || '';
  const result = contentType.includes('application/json')
    ? await response.json()
    : await response.text();

  if (!response.ok) {
    const message =
      typeof result === 'string'
        ? result
        : result?.message ||
          result?.detail ||
          result?.error ||
          'Failed to save summary notes';

    throw new Error(message);
  }

  return result;
};

export const checkPatientHasActiveCycle = async (
  patientId: string | number
): Promise<boolean> => {
  const token = getAccessToken();

  if (!token) {
    throw new Error('Authentication token not found. Please login again.');
  }

  const url = `${CYCLE_ACTIVE_CHECK_ENDPOINT}?patient_id=${encodeURIComponent(
    String(patientId)
  )}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(await parseApiError(response));
  }

  const contentType = response.headers.get('content-type') || '';
  const result = contentType.includes('application/json')
    ? await response.json()
    : await response.text();

  return extractIsCycleActive(result);
};

const humanize = (value?: string | null): string => {
  if (!value) return '';
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim();
};

export const parseSummaryAiNotes = (
  value?: string | null
): ParsedSummaryAiNotes | null => {
  if (!value) return null;

  try {
    return JSON.parse(value) as ParsedSummaryAiNotes;
  } catch {
    return null;
  }
};

export const resolveSummaryLogCreatedAt = (
  log: SummaryLogItem,
  parsed: ParsedSummaryAiNotes | null
): string => {
  return firstNonEmpty(log.created_at, parsed?.created_at);
};

export const formatCommunicationDateLabel = (
  value?: string | null
): string => {
  if (!value) return 'Unknown date';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown date';

  const today = new Date();
  const isToday =
    today.getFullYear() === date.getFullYear() &&
    today.getMonth() === date.getMonth() &&
    today.getDate() === date.getDate();

  if (isToday) return 'TODAY';

  return `${String(date.getDate()).padStart(2, '0')}/${String(
    date.getMonth() + 1
  ).padStart(2, '0')}/${date.getFullYear()}`;
};

export const formatCommunicationTimeLabel = (
  value?: string | null
): string => {
  if (!value) return '--:--';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--:--';

  return date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

export const getSummaryLogTitle = (
  log: SummaryLogItem,
  parsed: ParsedSummaryAiNotes | null
): string => {
  const label =
    firstNonEmpty(
      parsed?.summary_heading,
      parsed?.extraction_type?.label,
      log.source,
      humanize(log.document_type),
      log.type === 'voice_to_text' ? 'Voice to Text' : humanize(log.type)
    ) || 'Clinical Entry';

  return log.type === 'voice_to_text' ? `Voice to Text - ${label}` : label;
};

export const getSummaryLogSnippet = (
  log: SummaryLogItem,
  parsed: ParsedSummaryAiNotes | null
): string => {
  const summaryLine = Array.isArray(parsed?.summary_lines)
    ? parsed?.summary_lines?.find((line) => String(line).trim())
    : '';

  if (summaryLine) return summaryLine;

  const transcript = (log.call_transcript || '').replace(/\s+/g, ' ').trim();

  if (!transcript) return 'No transcript available.';

  return transcript.length > 160
    ? `${transcript.slice(0, 160).trim()}...`
    : transcript;
};

export const fetchPatientSummaryLogs = async (
  patientId: string | number
): Promise<SummaryLogItem[]> => {
  const token = getAccessToken();

  if (!token) {
    throw new Error('Authentication token not found. Please login again.');
  }

  const url = `${SAVE_SUMMARY_NOTES_ENDPOINT}/${encodeURIComponent(
    String(patientId)
  )}/summary-logs/`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(await parseApiError(response));
  }

  const contentType = response.headers.get('content-type') || '';
  const result = contentType.includes('application/json')
    ? await response.json()
    : [];

  if (Array.isArray(result)) {
    return result as SummaryLogItem[];
  }

  if (Array.isArray(result?.results)) {
    return result.results as SummaryLogItem[];
  }

  return [];
};